import { formatDistanceToNowStrict } from 'date-fns';
import { ChevronRight, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { dfLocale } from '@/lib/date-fns-locale';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';

import { useCallHistory } from '../../hooks/use-call-history';
import { useVoiceStore, type IHistoryDetailTarget } from '../../state/voice-ui.store';
import type { ICall } from '../../types';
import { CallerIdentity } from './caller-identity';

/** How close to the bottom (px) before the next page loads. */
const LOAD_MORE_THRESHOLD_PX = 80;

function directionIcon(call: Pick<ICall, 'direction' | 'answeredAt'>) {
    if (call.direction === 'OUTBOUND') {
        return <PhoneOutgoing className="size-3.5 shrink-0 text-text-tertiary" />;
    }
    if (call.answeredAt === null) {
        return <PhoneMissed className="size-3.5 shrink-0 text-error-500" />;
    }
    return <PhoneIncoming className="size-3.5 shrink-0 text-text-tertiary" />;
}

/** The other party of a call, from the org's point of view. */
function counterpartNumber(call: Pick<ICall, 'direction' | 'fromNumber' | 'toNumber'>): string {
    return call.direction === 'OUTBOUND' ? call.toNumber : call.fromNumber;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <p className="px-4 pt-3 pb-1 text-[10px] font-semibold tracking-[0.12em] text-text-secondary uppercase">
            {children}
        </p>
    );
}

interface HistoryPanelProps {
    /** Dial the counterpart — funnels through the widget's handleDial guards. */
    readonly onRedial: (toNumber: string) => void;
    /** Open the per-number detail view for the clicked row's counterpart. */
    readonly onOpenDetail: (target: IHistoryDetailTarget) => void;
    /** True when the widget has an explicit (resized) size — the list then
     *  fills the stretched body instead of capping at the design max-height. */
    readonly fill?: boolean;
}

/**
 * Widget History tab — every call on the selected caller-ID line (any
 * teammate), or all org calls when the caller ID is "Auto". CURRENT pins
 * the in-flight call plus any still-ringing queued calls; PREVIOUS is an
 * infinite, newest-first list where a row click opens that counterpart's
 * detail view — dialing happens ONLY via the row's dedicated phone button
 * (guarded while a call is active).
 */
export function HistoryPanel({ onRedial, onOpenDetail, fill = false }: HistoryPanelProps) {
    const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
    const canReadCalls = hasAnyPermission([PERMISSION.VOICE_CALLS_READ]);

    const activeCall = useVoiceStore((s) => s.activeCall);
    const ringingQueue = useVoiceStore((s) => s.ringingQueue);
    const selectedFromIdentityId = useVoiceStore((s) => s.selectedFromIdentityId);

    const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useCallHistory({
        channelIdentityId: selectedFromIdentityId ?? undefined,
        enabled: !permissionsLoading && canReadCalls,
    });

    if (!permissionsLoading && !canReadCalls) {
        return (
            <div className="p-6 text-center text-sm text-text-tertiary">
                You don&apos;t have permission to view call history
            </div>
        );
    }

    const hasCurrent = Boolean(activeCall) || ringingQueue.length > 0;

    // CURRENT calls also come back from the API — keep them only in CURRENT.
    const currentCallIds = new Set<string>([
        ...(activeCall ? [activeCall.callId] : []),
        ...ringingQueue.map((ringing) => ringing.callId),
    ]);
    const calls = (data?.pages.flatMap((page) => page.data) ?? []).filter(
        (call) => !currentCallIds.has(call.id),
    );

    const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        const el = event.currentTarget;
        const nearBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX;
        if (nearBottom && hasNextPage && !isFetchingNextPage) void fetchNextPage();
    };

    // The history query is disabled until permissions resolve, so `isLoading`
    // is false during that window — key the skeleton off `permissionsLoading`
    // too, otherwise the empty-state flashes before the query can even fire.
    if (isLoading || permissionsLoading) {
        return (
            <div className="flex flex-col gap-2 p-4">
                {Array.from({ length: 4 }, (_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                ))}
            </div>
        );
    }

    if (!hasCurrent && calls.length === 0) {
        return (
            <div className="p-6 text-center text-sm text-text-tertiary">
                {selectedFromIdentityId ? 'No calls on this number yet' : 'No calls yet'}
            </div>
        );
    }

    return (
        <div
            className={cn('flex min-h-0 flex-col overflow-y-auto', fill ? 'flex-1' : 'max-h-80')}
            onScroll={handleScroll}
        >
            {hasCurrent && (
                <>
                    <SectionLabel>Current</SectionLabel>
                    {activeCall && (
                        <div className="flex items-center justify-between gap-2 px-4 py-2.5">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                                {activeCall.direction === 'OUTBOUND' ? (
                                    <PhoneOutgoing className="size-3.5 shrink-0 text-success-600" />
                                ) : (
                                    <PhoneIncoming className="size-3.5 shrink-0 text-success-600" />
                                )}
                                <CallerIdentity
                                    name={activeCall.contact?.name ?? null}
                                    number={counterpartNumber(activeCall)}
                                    size="sm"
                                />
                            </div>
                            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-success-500/10 px-2 py-0.5 text-xs font-medium text-success-800 dark:text-success-300">
                                <span className="size-1.5 animate-pulse rounded-full bg-success-500" />
                                Live
                            </span>
                        </div>
                    )}
                    {ringingQueue.map((ringing) => (
                        <div
                            key={ringing.callId}
                            className="flex items-center justify-between gap-2 px-4 py-2.5"
                        >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                                <PhoneIncoming className="size-3.5 shrink-0 text-success-600" />
                                <CallerIdentity
                                    name={ringing.contact?.name ?? null}
                                    number={ringing.fromNumber}
                                    size="sm"
                                />
                            </div>
                            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-warning-500/10 px-2 py-0.5 text-xs font-medium text-warning-800 dark:text-warning-300">
                                <span className="size-1.5 animate-pulse rounded-full bg-warning-500" />
                                Ringing
                            </span>
                        </div>
                    ))}
                </>
            )}

            {calls.length > 0 && <SectionLabel>Previous</SectionLabel>}
            <div className="flex flex-col divide-y divide-white/40 dark:divide-white/[0.06]">
                {calls.map((call) => {
                    const number = counterpartNumber(call);
                    const name = call.contact
                        ? [call.contact.firstName, call.contact.lastName]
                              .filter(Boolean)
                              .join(' ') || null
                        : null;
                    return (
                        // Two sibling interactive elements — never nested
                        // buttons: the row opens the detail view (chevron is
                        // decorative), only the phone button dials.
                        <div
                            key={call.id}
                            className="flex items-center gap-1 pr-2 transition-colors hover:bg-white/50 dark:hover:bg-white/[0.05]"
                        >
                            <button
                                type="button"
                                aria-label={`View calls with ${name ?? number}`}
                                className="flex min-w-0 flex-1 items-center gap-2 py-2.5 pl-4 text-left focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-inset focus-visible:outline-none"
                                onClick={() =>
                                    onOpenDetail({
                                        number,
                                        contact: call.contact
                                            ? { id: call.contact.id, name: name ?? number }
                                            : null,
                                    })
                                }
                            >
                                {directionIcon(call)}
                                {/* min-w-0 flex-1 makes the identity the ONLY
                                    shrinkable piece — without it a long name
                                    pushes into the timestamp/chevron. */}
                                <CallerIdentity
                                    name={name}
                                    number={number}
                                    size="sm"
                                    className="min-w-0 flex-1"
                                />
                                <span className="shrink-0 text-[10px] text-text-tertiary">
                                    {formatDistanceToNowStrict(new Date(call.startedAt), {
                                        addSuffix: true,
                                        locale: dfLocale(),
                                    })}
                                </span>
                                <ChevronRight
                                    className="size-3.5 shrink-0 text-text-tertiary"
                                    aria-hidden="true"
                                />
                            </button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="shrink-0 text-text-tertiary hover:text-success-600"
                                disabled={Boolean(activeCall)}
                                title={
                                    activeCall ? 'Finish the current call first' : `Call ${number}`
                                }
                                aria-label={`Call ${number}`}
                                onClick={() => onRedial(number)}
                            >
                                <Phone className="size-3.5" />
                            </Button>
                        </div>
                    );
                })}
            </div>

            {isFetchingNextPage && (
                <div className="flex justify-center py-2">
                    <Spinner className="size-4 text-text-tertiary" />
                </div>
            )}
        </div>
    );
}
